import { useState } from 'react';
import { CircleHelp } from 'lucide-react';
import { Modal, TabPanels } from '@core/ui/Modal';
import { SegmentedControl } from '@core/ui/SegmentedControl';
import { CHANGELOG } from '../../../changelog';
import { longDate } from '@core/dates';

const GROUPS: [string, [string, string][]][] = [
  ['Tasks on the grid (Projects, People)', [
    ['Double-click / Enter', 'open the task card'],
    ['Drag', 'move by weeks; onto a person row — reassign (People)'],
    ['Shift + drag', 'move or resize by days; the day grid appears'],
    ['Drag an edge', 'resize'],
    ['← →  /  ↑ ↓', 'move by a week  /  to the row above or below'],
    ['Ctrl + ← →', 'move by a day'],
    ['Shift + ← →', 'longer or shorter by a week (with Ctrl — by a day)'],
    ['Ctrl + D  ·  Ctrl + P  ·  Delete', 'duplicate  ·  pin to the header  ·  delete'],
    ['Right-click', 'open, ask a question, duplicate, pin, highlight, backlog, delete'],
  ]],
  ['Several tasks', [
    ['Ctrl + click', 'add to the selection / remove from it'],
    ['Drag on empty grid', 'select with a frame (with Ctrl — add to the selection)'],
    ['Drag a selected task, ← →', 'move all selected by the same days; rows stay'],
    ['Right-click a selected task', 'duplicate, backlog or delete all selected'],
    ['Esc / click on empty grid', 'clear the selection'],
  ]],
  ['Launch Board', [
    ['Drag', 'move by days, onto another track; an edge — resize'],
    ['← →  /  ↑ ↓', 'move by a day  /  to the next track'],
    ['Shift + ← →', 'longer or shorter by a day'],
    ['Right-click', 'open, duplicate, delete'],
    ['Rows', 'rename (double-click or ✎), + track / + task, ⋮⋮ reorder, colour by the dot'],
    ['Blocked by', 'in the task card; arrows show who blocks whom'],
  ]],
  ['Rows and header', [
    ['Right-click a row or empty grid', 'quick task here, new task here, edit, hide (only for you)'],
    ['Right-click an event, vacation or pinned task', 'edit, highlight, jump to the task, hide the lane'],
    ['Double-click a pill or the 🏖 label', 'edit'],
    ['⋮⋮ drag', 'reorder projects or people — only for you'],
    ['Backlog / Design / Agenda', 'side panel; click again to hide'],
  ]],
];

type HelpTab = 'shortcuts' | 'news';
const TABS: [HelpTab, string][] = [['shortcuts', 'Shortcuts'], ['news', "What's new"]];

// Окно «?»: справка по хоткеям и «What's new» — что изменилось для пользователей с прошлой выкатки.
export function HotkeysButton() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<HelpTab>('shortcuts');
  return (
    <>
      <button type="button" className="min-h-0 p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-800" onClick={() => setOpen(true)} title="Help: shortcuts and what's new" aria-label="Help">
        <CircleHelp className="w-5 h-5" />
      </button>
      {open && (
        <Modal
          open onClose={() => setOpen(false)} title="Help" width="max-w-3xl"
          head={<SegmentedControl role="tabs" label="Help sections" className="mb-4" items={TABS.map(([key, label]) => ({ key, label }))} value={tab} onChange={setTab} />}
        >
          <TabPanels active={tab} panels={[['news', (
            <div className="flex flex-col gap-5">
              {CHANGELOG.map((e) => (
                <section key={`${e.date} ${e.name}`}>
                  <h3 className="font-semibold mb-2">{longDate(e.date)} · Release v.{e.version} “{e.name}”</h3>
                  {([['New', e.added], ['Changed', e.changed], ['Fixed', e.fixed]] as const).map(([title, items]) => items?.length ? (
                    <div key={title} className="mb-2">
                      <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">{title}</h4>
                      <ul className="list-disc pl-5 text-sm text-gray-800 flex flex-col gap-1">
                        {items.map((it) => <li key={it}>{it}</li>)}
                      </ul>
                    </div>
                  ) : null)}
                </section>
              ))}
            </div>
          )], ['shortcuts', (
          <div className="flex flex-col gap-4">
            {GROUPS.map(([title, rows]) => (
              <section key={title}>
                <h3 className="font-semibold mb-1">{title}</h3>
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                  {rows.map(([k, v]) => (
                    <div key={k} className="contents">
                      <dt className="font-mono whitespace-nowrap text-gray-800">{k}</dt>
                      <dd className="text-gray-600">{v}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            ))}
          </div>
          )]]} />
        </Modal>
      )}
    </>
  );
}
